import React, { useContext, useEffect, useState } from "react";
import IntroModal from "./IntroModal";
import ResetTutorialConfirmModal from "./ResetTutorialConfirmModal";
import TimeMachineTour from "./TimeMachineTour";
import { AppContext } from "../../context/app.context";
import { ModelCxContext } from "../../context/modelCx.context";
import { canAccessTimeMachine } from "../../utility/canAccessTimeMachine";
import { isTutorialCompleted } from "../../utility/tutorialStateHelpers";
import { buildSetTutorialStatePayload } from "../../utility/buildSetTutorialStatePayload";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #124 react-joyride Time Machine tutorial — top-level orchestrator.
//
// Single component App.js renders once. Owns the state machine for:
//   - whether to show the IntroModal on (re)login
//   - whether the TimeMachineTour is running
//   - whether the ResetTutorialConfirmModal is open (state lifted to
//     AppContext so the Header avatar menu can trigger it)
//
// Persistence:
//   - "Don't Show Again" / tour completion → setUserById writes
//     tutorialState.timeTravel.completed = true to the Users doc.
//   - "Maybe Later" → sessionStorage flag suppresses the intro for the
//     rest of this browser session. Survives tab refresh; resets on
//     browser restart. Matches the user's "users almost never log out
//     but do restart their computer daily" workflow.
//
// Tutorial key is hardcoded "timeTravel" for v1; future tutorials can
// add their own orchestrator instances with different keys.

const TUTORIAL_KEY = "timeTravel";
const SESSION_DISMISS_KEY = `tutorialIntroDismissed-${TUTORIAL_KEY}`;

const TutorialOrchestrator = () => {
  const {
    openResetTutorialConfirm,
    setOpenResetTutorialConfirm,
  } = useContext(AppContext) || {};
  // mongoUser + tsUserDocument both live on ModelCxContext (not
  // AppContext). Header.js reads them the same way.
  const { mongoUser, tsUserDocument, setUserById } =
    useContext(ModelCxContext) || {};

  const [introOpen, setIntroOpen] = useState(false);
  const [tourRunning, setTourRunning] = useState(false);

  // - - - - - - decide whether to show the intro on (re)login
  //
  // Fires as soon as both mongoUser and tsUserDocument are populated.
  // Only opens the intro if (a) the user has Time Travel access, AND
  // (b) they haven't already completed the tutorial, AND (c) they
  // haven't dismissed it in this session.
  useEffect(() => {
    if (!mongoUser || !tsUserDocument) return;
    if (!canAccessTimeMachine(mongoUser, tsUserDocument)) return;
    if (isTutorialCompleted(tsUserDocument, TUTORIAL_KEY)) return;
    if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") return;
    setIntroOpen(true);
  }, [mongoUser, tsUserDocument]);

  // - - - - - - persistence helper
  //
  // The Users-collection `username` field is the user's email — that's
  // what `setUserById` filters on per the existing prefs-auto-save
  // pattern in modelCx.context.js (`filter: { username: userEmail }`).
  // `userEmail` comes from MSAL via `mongoUser._profile.data.email`,
  // NOT from `mongoUser.username` (which is undefined or different).
  const userEmail = mongoUser?._profile?.data?.email;
  const writeCompletion = (completed) => {
    if (!userEmail || typeof setUserById?.mutate !== "function") return;
    const payload = buildSetTutorialStatePayload({
      username: userEmail,
      tutorialKey: TUTORIAL_KEY,
      completed,
      changedBy: userEmail,
    });
    setUserById.mutate(payload);
  };

  // - - - - - - intro modal handlers
  const handleTakeTour = () => {
    setIntroOpen(false);
    setTourRunning(true);
  };
  const handleMaybeLater = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setIntroOpen(false);
  };
  const handleDontShowAgain = () => {
    writeCompletion(true);
    setIntroOpen(false);
  };

  // - - - - - - tour handlers
  const handleTourComplete = (_status) => {
    writeCompletion(true);
    setTourRunning(false);
  };
  const handleTourClose = () => {
    // User clicked X mid-tour — don't mark complete (let them re-take).
    // Also clear the session-dismiss flag so the intro modal can fire
    // again on a fresh session, but not in this one — they just saw it.
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setTourRunning(false);
  };

  // - - - - - - reset-confirm handlers (triggered from Header menu)
  const closeResetConfirm = () =>
    typeof setOpenResetTutorialConfirm === "function" &&
    setOpenResetTutorialConfirm({ show: false });

  const handleResetTakeNow = () => {
    writeCompletion(false);
    sessionStorage.removeItem(SESSION_DISMISS_KEY);
    closeResetConfirm();
    setTourRunning(true);
  };
  const handleResetForNextLogin = () => {
    writeCompletion(false);
    sessionStorage.removeItem(SESSION_DISMISS_KEY);
    closeResetConfirm();
  };
  const handleResetCancel = () => {
    closeResetConfirm();
  };

  return (
    <>
      <IntroModal
        open={introOpen}
        onTakeTour={handleTakeTour}
        onMaybeLater={handleMaybeLater}
        onDontShowAgain={handleDontShowAgain}
      />
      <ResetTutorialConfirmModal
        open={openResetTutorialConfirm?.show === true}
        onTakeNow={handleResetTakeNow}
        onResetForNextLogin={handleResetForNextLogin}
        onCancel={handleResetCancel}
      />
      <TimeMachineTour
        run={tourRunning}
        onComplete={handleTourComplete}
        onClose={handleTourClose}
      />
    </>
  );
};

export default TutorialOrchestrator;
